import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import SEOHead from '@/components/base/SEOHead';
import StructuredData from '@/components/base/StructuredData';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.category === article.category && a.slug !== article.slug)
            .slice(0, 3)
        : [],
    [article]
  );

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 mb-3 font-[family-name:var(--font-heading)]">
              文章未找到
            </h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 inline-flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryLabel =
    article.category === 'profile' ? '个人资料' :
    article.category === 'photobooks' ? '写真集' :
    article.category === 'film-tv' ? '影视作品' :
    article.category === 'news' ? '新闻动态' : '音乐作品';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} | ${siteConfig.name}`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},${siteConfig.keywords}`}
        canonicalUrl={`${siteConfig.url}/article/${article.slug}`}
        ogImage={article.coverImage}
        ogType="article"
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
      />
      <StructuredData data={articleSchema} />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryLabel, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="py-8 md:py-10">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="mb-8">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 mb-4 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  {categoryLabel}
                </Link>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight font-[family-name:var(--font-heading)]">
                  {article.title}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    阅读时间约{article.readTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {Math.floor(Math.random() * 50000) + 10000} 次阅读
                  </span>
                </div>
              </div>

              <div className="rounded-lg overflow-hidden mb-10">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="prose prose-sm md:prose-base max-w-none">
                {article.content.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-10 pt-6 border-t border-background-200">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200/70"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-500">
                    <i className="ri-user-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{siteConfig.name}</p>
                    <p className="text-xs text-foreground-500">编辑团队</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">分享到：</span>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(`${siteConfig.url}/article/${article.slug}`)}`}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-900 text-background-50 hover:bg-primary-500 transition-colors duration-200 cursor-pointer"
                    aria-label="分享到Twitter"
                  >
                    <i className="ri-twitter-x-line text-xs"></i>
                  </a>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-14 md:py-16 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8 font-[family-name:var(--font-heading)]">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}